/**
 * レティクルによる「調べる」対象の判定と、Pointer Lock の管理。
 *
 * Pointer Lock は解除直後に再要求すると（ブラウザの安全策で）失敗することがある。
 * 失敗を握りつぶさず「クリックで再開」を表示する状態に落とすことで、
 * 「解除後に操作不能になる」不具合を防ぐ。
 */

import * as THREE from 'three';
import type { InteractableHandle } from './scene';

/** レティクルが届く最大距離（m）。テストも本番と同じ値を使う */
export const MAX_INTERACT_DISTANCE = 3.6;

export interface TargetInfo {
  handle: InteractableHandle;
  distance: number;
}

export class InteractionRaycaster {
  private raycaster = new THREE.Raycaster();
  private readonly center = new THREE.Vector2(0, 0);

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  /** 画面中央のレティクルが指している対象を返す */
  pick(handles: InteractableHandle[], maxDistance = MAX_INTERACT_DISTANCE): TargetInfo | null {
    if (handles.length === 0) return null;
    this.raycaster.setFromCamera(this.center, this.camera);
    this.raycaster.far = maxDistance;

    // 狙う相手は各グループの「当たり判定ボックス」だけ。
    // 見た目のメッシュ（PC の机、ラックの筐体など）をレイキャストの対象にすると、
    // 飾りが中身の手前に立ちはだかって「見えているのに狙えない」対象が生まれる。
    const objects = handles.map((h) => h.pickTarget ?? h.object);
    const hits = this.raycaster.intersectObjects(objects, true);
    for (const hit of hits) {
      const handle = this.findHandle(handles, hit.object);
      if (handle) return { handle, distance: hit.distance };
    }
    return null;
  }

  private findHandle(handles: InteractableHandle[], object: THREE.Object3D): InteractableHandle | null {
    let node: THREE.Object3D | null = object;
    while (node) {
      const id = node.userData?.interactableId;
      if (typeof id === 'string') {
        return handles.find((h) => h.id === id) ?? null;
      }
      node = node.parent;
    }
    return null;
  }
}

export type PointerLockState = 'locked' | 'unlocked' | 'needs-click';

export class PointerLockManager {
  private state: PointerLockState = 'unlocked';
  private listeners = new Set<(state: PointerLockState) => void>();
  private disposers: (() => void)[] = [];
  /** ユーザーが意図的に解除したか（ポーズ扱いにするため） */
  private intentionalRelease = false;

  constructor(private readonly element: HTMLElement) {
    const onChange = (): void => {
      const locked = document.pointerLockElement === this.element;
      this.setState(locked ? 'locked' : this.intentionalRelease ? 'unlocked' : 'needs-click');
      this.intentionalRelease = false;
    };
    const onError = (): void => {
      // 解除直後の再要求はブラウザに拒否される。クリック待ちに落とす
      this.setState('needs-click');
    };
    document.addEventListener('pointerlockchange', onChange);
    document.addEventListener('pointerlockerror', onError);
    this.disposers.push(() => document.removeEventListener('pointerlockchange', onChange));
    this.disposers.push(() => document.removeEventListener('pointerlockerror', onError));
  }

  get current(): PointerLockState {
    return this.state;
  }

  get isLocked(): boolean {
    return this.state === 'locked';
  }

  onChange(listener: (state: PointerLockState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** ユーザー操作（クリックやキー入力）の中から呼ぶこと */
  request(): void {
    if (this.state === 'locked') return;
    try {
      const result = this.element.requestPointerLock() as unknown;
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => this.setState('needs-click'));
      }
    } catch {
      this.setState('needs-click');
    }
  }

  /** UI を開くときなど、こちらから解除する場合 */
  release(): void {
    this.intentionalRelease = true;
    if (document.pointerLockElement === this.element) {
      document.exitPointerLock();
    } else {
      this.setState('unlocked');
      this.intentionalRelease = false;
    }
  }

  private setState(next: PointerLockState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of [...this.listeners]) listener(next);
  }

  dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    this.listeners.clear();
  }
}
